import { jolieConfig as config } from "../config/jolie";
import type { Viewport } from "./behavior";

type EntrancePhase = "entry" | "landing" | "landing_fast";
type EntranceAtlas = {
	frames: Record<string, { duration: number; sourceSize: { w: number } }>;
	animations: Record<string, { frames: string[] }>;
};

const phases: EntrancePhase[] = ["entry", "landing", "landing_fast"];

export class JolieEntrance {
	phase: EntrancePhase | "done" = "entry";
	x = 0;
	height = 0;
	private elapsedMs = 0;

	constructor(private atlas: EntranceAtlas) {}

	get active() {
		return this.phase !== "done";
	}

	private duration(phase: EntrancePhase) {
		return this.atlas.animations[phase].frames.reduce(
			(total, name) => total + this.atlas.frames[name].duration,
			0,
		);
	}

	get frameName() {
		const phase = this.phase === "done" ? "landing_fast" : this.phase;
		const frames = this.atlas.animations[phase].frames;
		let time = this.elapsedMs;
		for (const name of frames) {
			time -= this.atlas.frames[name].duration;
			if (time < 0) return name;
		}
		return frames[frames.length - 1];
	}

	update(deltaMs: number, viewport: Viewport, reducedMotion = false) {
		if (!this.active) return;
		const margin = Math.min(
			viewport.width / 2,
			config.maximumHalfWidth * config.displayScale +
				config.edgePaddingPx,
		);
		const targetX = Math.max(
			margin,
			Math.min(
				viewport.width - margin,
				viewport.width -
					Math.min(
						config.entrance.landingInsetPx,
						viewport.width * 0.35,
					),
			),
		);
		if (reducedMotion) this.phase = "done";
		else {
			this.elapsedMs += Math.max(
				0,
				Math.min(deltaMs, config.maxFrameDeltaMs),
			);
			while (
				this.phase !== "done" &&
				this.elapsedMs >= this.duration(this.phase)
			) {
				this.elapsedMs -= this.duration(this.phase);
				this.phase = phases[phases.indexOf(this.phase) + 1] ?? "done";
			}
		}
		this.x = targetX;
		this.height = 0;
		if (this.phase === "entry") {
			const progress = this.elapsedMs / this.duration("entry");
			const startX =
				viewport.width +
				(this.atlas.frames[this.frameName].sourceSize.w *
					config.displayScale) /
					2;
			this.x = startX + (targetX - startX) * progress;
			this.height =
				Math.min(config.entrance.fallHeightPx, viewport.ground * 0.35) *
				(1 - progress * progress);
		}
	}
}
